import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft,
  Tag,
  Info,
  QrCode,
  Check,
  Copy,
  Download,
  Loader2,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { catalogService } from "../../../services/catalogService";
import { toast } from "sonner";
import { downloadQrPng } from "../../../utils/codeImages";
import type { Product } from "../../../types";
import {
  ProductEditorModal,
  type ProductFormValues,
} from "../../components/ProductEditorModal";

const MAX_PRODUCTS = 5;

export function ExhibitorProductsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ mode: "create" | "edit"; product?: Product } | null>(
    null,
  );

  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";
  const canAdd = products.length < MAX_PRODUCTS;

  const loadProducts = useCallback(async () => {
    if (!eventId || !companyId) return;
    setLoading(true);
    try {
      const list = await catalogService.listProducts(eventId, companyId, "admin");
      setProducts(list);
    } catch {
      toast.error("Не удалось загрузить товары");
    } finally {
      setLoading(false);
    }
  }, [eventId, companyId]);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  const handleSave = async (data: ProductFormValues) => {
    if (!eventId || !companyId) return;
    const payload = {
      name: data.name,
      category: data.category || undefined,
      description: data.description || undefined,
      price: data.price || undefined,
      ...(data.clearImage
        ? { images: [] as string[] }
        : data.images !== undefined
          ? { images: data.images }
          : {}),
    };
    if (modal?.mode === "edit" && modal.product) {
      await catalogService.updateProduct(eventId, companyId, modal.product.id, payload);
      toast.success("Товар обновлён");
    } else {
      await catalogService.createProduct(eventId, companyId, payload);
      toast.success("Товар добавлен");
    }
    await loadProducts();
  };

  const handleDelete = async (product: Product) => {
    if (!eventId || !companyId) return;
    if (!window.confirm(`Удалить товар «${product.name}»?`)) return;
    try {
      await catalogService.deleteProduct(eventId, companyId, product.id);
      toast.success("Товар удалён");
      await loadProducts();
    } catch {
      toast.error("Не удалось удалить товар");
    }
  };

  const handleCopyQr = (code: string, id: string) => {
    if (!code) return;
    void navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadQr = async (product: Product) => {
    if (!product.qrCode) {
      toast.error("QR для товара ещё не создан");
      return;
    }
    setDownloadingId(product.id);
    try {
      await downloadQrPng(product.qrCode, `${product.name}-product`);
      toast.success("QR сохранён");
    } catch {
      toast.error("Не удалось скачать QR");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      {modal ? (
        <ProductEditorModal
          mode={modal.mode === "create" ? "create" : "edit"}
          initial={modal.product}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/exhibitor")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[19px] text-gray-900">Товары на выставке</h1>
          <p className="text-[12px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span>Количество позиций: {products.length}</span>
            <span className="text-gray-300" aria-hidden>
              ·
            </span>
            <span>Скачайте QR для образцов на стенде</span>
            <button
              type="button"
              onClick={() => navigate("/exhibitor/qr")}
              className="text-indigo-600 hover:text-indigo-800 font-medium underline-offset-2 hover:underline"
            >
              Все QR
            </button>
          </p>
        </div>
        {canAdd ? (
          <button
            type="button"
            onClick={() => setModal({ mode: "create" })}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px]"
          >
            <Plus size={14} /> Добавить
          </button>
        ) : null}
      </div>

      <div className="p-3.5 rounded-xl bg-indigo-50 border border-indigo-100 text-[12px] text-indigo-700 flex items-start gap-2">
        <Info size={13} className="mt-0.5 flex-shrink-0" />
        <span>
          Вы скачиваете и распечатываете QR товаров, чтобы посетители могли отсканировать образец и
          сохранить описание. При необходимости можно исправить информацию, нажав иконку
          редактирования.
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center">
          <Tag size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-[14px] text-gray-500">Товаров пока нет</p>
          <p className="text-[13px] text-gray-400 mt-1 mb-4">Добавьте первый товар для каталога</p>
          {canAdd ? (
            <button
              type="button"
              onClick={() => setModal({ mode: "create" })}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-[13px]"
            >
              <Plus size={14} /> Добавить товар
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
            >
              <div className="flex items-start gap-3 p-4">
                {product.images[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Tag size={22} className="text-indigo-300" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-gray-900">{product.name}</div>
                  {product.category ? (
                    <div className="text-[12px] text-indigo-600">{product.category}</div>
                  ) : null}
                  {product.description ? (
                    <p className="text-[12px] text-gray-500 mt-1 line-clamp-2 leading-snug">
                      {product.description}
                    </p>
                  ) : null}
                  {product.price ? (
                    <div className="inline-flex items-center mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {product.price}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", product })}
                    className="p-2 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600"
                    title="Редактировать"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(product)}
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"
                    title="Удалить"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {product.qrCode ? (
                <div className="border-t border-gray-50 px-4 py-2.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <QrCode size={12} className="text-indigo-400 flex-shrink-0" />
                    <span className="text-[11px] text-gray-500">QR товара</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopyQr(product.qrCode!, product.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 hover:bg-indigo-50 text-gray-500 hover:text-indigo-600 text-[11px]"
                    >
                      {copiedId === product.id ? <Check size={11} /> : <Copy size={11} />}
                      {copiedId === product.id ? "OK" : "Копировать"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDownloadQr(product)}
                      disabled={downloadingId === product.id}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[11px] disabled:opacity-60"
                    >
                      {downloadingId === product.id ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Download size={11} />
                      )}
                      PNG
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {!canAdd && products.length > 0 ? (
        <p className="text-[12px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2 text-center">
          Достигнут лимит {MAX_PRODUCTS} товаров. Удалите позицию, чтобы добавить новую.
        </p>
      ) : null}
    </div>
  );
}
